import {Toggle} from '../toggle';

type Options = Partial<FaqAccordionsOptions>;

export interface FaqAccordionsElement extends HTMLElement {
  faqAccordions?: FaqAccordions;
}

interface FaqAccordionsOptions {
  activeClass: string;
  primarySelector: string;
  secondaryActiveClass: string;
  secondarySelector: string;
  secondaryTriggerSelector: string;
  secondaryContentSelector: string;
}

interface PrimaryAccordion {
  childAccordions: Element[];
  trigger: HTMLElement;
}

interface SecondaryAccordion {
  element: HTMLElement;
  toggle?: Toggle;
}

export class FaqAccordions {
  static defaults: FaqAccordionsOptions = {
    activeClass: 'is-active',
    primarySelector: '[data-faq-accordion_primary]',
    secondaryActiveClass: 'is-open',
    secondaryContentSelector: '[data-faq-accordion-content-secondary]',
    secondarySelector: '[data-faq-accordion_secondary]',
    secondaryTriggerSelector: '[data-faq-accordion-toggle-secondary]',
  };
  element: FaqAccordionsElement;
  options: FaqAccordionsOptions;
  primaryAccordions: PrimaryAccordion[];
  secondaryAccordions: SecondaryAccordion[];

  constructor(element: FaqAccordionsElement, options: Options) {
    if (!element || element.faqAccordions) {
      return;
    }

    this.element = element;
    this.options = {
      ...FaqAccordions.defaults,
      ...this.element.dataset,
      ...options,
    };
    this.primaryAccordions = [];
    this.secondaryAccordions = [];

    this.setupAccordions();
  }

  setupAccordions() {
    const primaryAccordionElements = this.element.querySelectorAll(
      this.options.primarySelector,
    );
    const secondaryAccordionElements = this.element.querySelectorAll(
      this.options.secondarySelector,
    );

    if (!primaryAccordionElements || !secondaryAccordionElements) {
      return;
    }

    this.setupPrimaryAccordions(primaryAccordionElements);
    this.setupSecondaryAccordions(secondaryAccordionElements);
  }

  setupPrimaryAccordions = (primaryHeaders: NodeList) => {
    primaryHeaders.forEach(primaryHeader => {
      const childAccordions = this.selectSiblingsUntil(
        primaryHeader as Element,
        this.options.primarySelector,
      );

      if (childAccordions) {
        this.primaryAccordions.push({
          childAccordions,
          trigger: primaryHeader as HTMLElement,
        });
      }
    });

    this.primaryAccordions.forEach(primaryAccordion => {
      this.addPrimaryListeners(primaryAccordion);
    });
  };

  setupSecondaryAccordions = (secondaryAccordionElements: NodeList) => {
    secondaryAccordionElements.forEach(node => {
      if (!node) {
        return;
      }

      const element = node as HTMLElement;
      this.secondaryAccordions.push({element});
    });

    if (!this.secondaryAccordions) {
      return;
    }

    this.secondaryAccordions.forEach(accordion => {
      accordion.toggle = new Toggle(accordion.element, {
        activeClass: this.options.secondaryActiveClass,
        target: this.options.secondaryContentSelector,
        trigger: this.options.secondaryTriggerSelector,
      });
    });
  };

  selectSiblingsUntil(element: Element, untilSelector: string): Element[] {
    const siblings: Element[] = [];
    let nextSibling = element.nextElementSibling as Element;

    while (nextSibling) {
      if (nextSibling.matches(untilSelector)) {
        break;
      }

      siblings.push(nextSibling);
      nextSibling = nextSibling.nextElementSibling as Element;
    }

    return siblings;
  }

  addPrimaryListeners = (accordion: PrimaryAccordion) => {
    accordion.trigger.addEventListener('click', () => {
      this.togglePrimaryAccordion(accordion);
    });

    accordion.trigger.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.code === 'Space' || event.code === 'Enter') {
        event.preventDefault();
        this.togglePrimaryAccordion(accordion);
      }
    });
  };

  togglePrimaryAccordion(accordion: PrimaryAccordion) {
    accordion.trigger.classList.toggle(this.options.activeClass);
    this.toggleChildAccordions(accordion);
  }

  toggleChildAccordions(primaryAccordion: PrimaryAccordion) {
    primaryAccordion.childAccordions.forEach(childAccordion => {
      childAccordion.classList.toggle(this.options.activeClass);
    });
  }

  destroy() {
    this.element.faqAccordions = undefined;
  }
}
